import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customerService } from '../../../services/api';

const PAGE_SIZE = 200;

const fetchLeadsPage = ({ pageParam = 0, queryOptions = {} }) =>
  customerService
    .getConversations({
      paginationModel: { page: pageParam, pageSize: PAGE_SIZE },
      ...queryOptions,
    })
    .then((res) => res.data);

export const useLeadsQuery = (queryOptions) => {
  return useInfiniteQuery({
    queryKey: ['leads', queryOptions],
    queryFn: ({ pageParam }) => fetchLeadsPage({ pageParam, queryOptions }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length : undefined,
    staleTime: 10_000,
    gcTime: 60_000,
    refetchInterval: 2_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
};

const PICKER_PAGE_SIZE = 30;
const LEAD_PICKER_KEY = ['leadPicker'];

// Leads paginados para as telas de lista: os membros de uma lista
// (`listUuid`) ou os candidatos da busca ao adicionar. Página pequena e
// rolagem infinita: uma lista pode ter muitos leads.
export const useLeadPickerQuery = ({ search = '', listUuid } = {}) =>
  useInfiniteQuery({
    queryKey: [...LEAD_PICKER_KEY, { search, listUuid: listUuid ?? null }],
    queryFn: ({ pageParam }) =>
      customerService
        .getConversations({
          search,
          paginationModel: { page: pageParam, pageSize: PICKER_PAGE_SIZE },
          ...(listUuid && {
            filterModel: { items: [{ field: 'list', operator: '=', value: listUuid }] },
          }),
        })
        .then((res) => res.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length : undefined),
    placeholderData: keepPreviousData,
  });

export const useAcceptLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.acceptConversation(uuid) });

export const useReleaseLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.releaseConversation(uuid) });

export const useCloseLead = () =>
  useMutation({
    mutationFn: (uuid) =>
      customerService.closeConversation(uuid, { reason_code: 'CLOSED_BY_COLLABORATOR' }),
  });

export const useReopenLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.reopenConversation(uuid) });

export const usePinLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.pinConversation(uuid) });

export const useUnpinLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.unpinConversation(uuid) });

export const useReorderPins = () =>
  useMutation({ mutationFn: (uuids) => customerService.reorderPins(uuids) });

export const useFavoriteLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.favoriteConversation(uuid) });

export const useUnfavoriteLead = () =>
  useMutation({ mutationFn: (uuid) => customerService.unfavoriteConversation(uuid) });

const LEAD_LISTS_KEY = ['leadLists'];

export const useLeadListsQuery = () =>
  useQuery({
    queryKey: LEAD_LISTS_KEY,
    queryFn: () => customerService.getLeadLists().then((res) => res.data),
    staleTime: 30_000,
  });

// Toda mutação de lista invalida a query das listas; a de leads já faz polling.
const useLeadListMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEAD_LISTS_KEY });
      queryClient.invalidateQueries({ queryKey: LEAD_PICKER_KEY });
    },
  });
};

export const useCreateLeadList = () =>
  useLeadListMutation((data) => customerService.createLeadList(data).then((res) => res.data));

export const useUpdateLeadList = () =>
  useLeadListMutation(({ uuid, ...data }) => customerService.updateLeadList(uuid, data));

export const useDeleteLeadList = () =>
  useLeadListMutation((uuid) => customerService.deleteLeadList(uuid));

export const useAddLeadToList = () =>
  useLeadListMutation(({ listUuid, leadUuid }) => customerService.addLeadToList(listUuid, leadUuid));

export const useRemoveLeadFromList = () =>
  useLeadListMutation(({ listUuid, leadUuid }) => customerService.removeLeadFromList(listUuid, leadUuid));
